import type { Project, SupportGrant, SupportKey, SupportLevel, SupportOverview } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, KeyRound, Lock, LockOpen, ShieldCheck, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, del, get, post } from "@/api/client.ts";
import { projectsApi } from "@/api/projects.ts";
import { cn } from "@/lib/cn.ts";
import { copyText } from "@/lib/uid.ts";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";
import { Alert, Button, Card, CodeInput, Field, Input, Select, Spinner } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";

const supportApi = {
  overview: () => get<SupportOverview>("/api/support"),
  createKey: (name: string) => post<{ key: string; record: SupportKey }>("/api/support/keys", { name }),
  revokeKey: (id: string) => del(`/api/support/keys/${id}`),
  unlock: (input: { keyId: string; level: SupportLevel; projectId: string | null; minutes: number; code: string }) => post<SupportGrant>("/api/support/unlock", input),
  lock: (grantId: string | null) => post<void>("/api/support/lock", grantId ? { grantId } : {}),
  setMinutes: (defaultMinutes: number) => api<void>("/api/support/settings", { method: "PUT", json: { defaultMinutes } }),
};

const minutesLeft = (iso: string) => Math.max(0, Math.ceil((Date.parse(iso) - Date.now()) / 60_000));

/**
 * Support access (owner only): Codvero Lab reads, and when allowed acts on, this platform's projects and chats with a
 * support key. The key opens nothing by itself: it is unlocked here for a while with an authenticator code.
 */
export function SupportSettings() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toMsg = useApiError();
  const { confirm } = useConfirm();
  const q = useQuery({ queryKey: ["support"], queryFn: supportApi.overview, refetchInterval: 15_000 });
  const projects = useQuery({ queryKey: ["projects"], queryFn: projectsApi.list }).data ?? [];
  const refresh = () => void qc.invalidateQueries({ queryKey: ["support"] });
  // an agent's "unlock" link brings what it asked for: ?support=read|act&project=<id>
  const asked = new URLSearchParams(location.search);
  const [level, setLevel] = useState<SupportLevel>(asked.get("support") === "act" ? "act" : "read");
  const [projectId, setProjectId] = useState<string>(asked.get("project") ?? "");
  const [keyId, setKeyId] = useState("");
  const [minutes, setMinutes] = useState<number | null>(null);
  const [code, setCode] = useState("");
  const [newName, setNewName] = useState("Codvero Lab");
  const [shownKey, setShownKey] = useState<string | null>(null);
  const [defMinutes, setDefMinutes] = useState<number | null>(null);
  const [, tick] = useState(0);
  useEffect(() => { const i = setInterval(() => tick((n) => n + 1), 30_000); return () => clearInterval(i); }, []);

  const live = (q.data?.keys ?? []).filter((k) => !k.revokedAt);
  const chosenKey = keyId || live[0]?.id || "";
  const unlock = useMutation({ mutationFn: () => supportApi.unlock({ keyId: chosenKey, level, projectId: projectId || null, minutes: minutes ?? q.data?.defaultMinutes ?? 30, code }), onSuccess: () => { setCode(""); refresh(); } });
  const lock = useMutation({ mutationFn: (id: string | null) => supportApi.lock(id), onSuccess: refresh });
  const create = useMutation({ mutationFn: () => supportApi.createKey(newName.trim()), onSuccess: (r) => { setShownKey(r.key); refresh(); } });
  const revoke = useMutation({ mutationFn: (id: string) => supportApi.revokeKey(id), onSuccess: refresh });
  const saveMinutes = useMutation({ mutationFn: (m: number) => supportApi.setMinutes(m), onSuccess: () => { setDefMinutes(null); refresh(); } });

  if (q.isLoading) return <Spinner />;
  if (q.error) return <Alert tone="danger">{toMsg(q.error)}</Alert>;
  const o = q.data!;

  return (
    <div className="space-y-4">
      {!o.totpEnabled && <Alert tone="warn">{t("support.needTotp")}</Alert>}

      {/* open windows */}
      <Card className="space-y-3 p-4">
        <div className="flex items-center gap-2">
          {o.active.length ? <LockOpen className="size-4 text-warning" /> : <Lock className="size-4 text-success" />}
          <h3 className="flex-1 text-sm font-medium">{o.active.length ? t("support.openNow", { count: o.active.length }) : t("support.allLocked")}</h3>
          {o.active.length > 1 && <Button size="sm" variant="secondary" loading={lock.isPending} onClick={() => lock.mutate(null)}><Lock className="size-3.5" /> {t("support.lockAll")}</Button>}
        </div>
        {o.active.map((g) => (
          <div key={g.id} className="flex flex-wrap items-center gap-2 rounded-md bg-surface-2 px-3 py-2 text-sm">
            <span className={cn("rounded px-1.5 py-0.5 text-[11px]", g.level === "act" ? "bg-danger/15 text-danger" : "bg-accent-soft text-accent")}>{t(`support.level.${g.level}`)}</span>
            <span dir="auto" className="font-medium">{g.keyName}</span>
            <span className="text-fg-muted">· {g.projectName ?? t("support.allProjects")}</span>
            <span className="ms-auto text-xs text-fg-muted">{t("support.minutesLeft", { count: minutesLeft(g.expiresAt) })}</span>
            <Button size="sm" variant="ghost" loading={lock.isPending} onClick={() => lock.mutate(g.id)}><Lock className="size-3.5" /> {t("support.lock")}</Button>
          </div>
        ))}
      </Card>

      {/* unlock */}
      {live.length > 0 && o.totpEnabled && (
        <Card className="p-4">
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); if (code.length === 6) unlock.mutate(); }}>
            <h3 className="flex items-center gap-2 text-sm font-medium"><ShieldCheck className="size-4 text-accent" /> {t("support.unlockTitle")}</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {live.length > 1 && (
                <Field label={t("support.key")}>
                  <Select value={chosenKey} onChange={(e) => setKeyId(e.target.value)}>{live.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}</Select>
                </Field>
              )}
              <Field label={t("support.levelLabel")}>
                <div className="flex gap-1">
                  {(["read", "act"] as const).map((l) => (
                    <button key={l} type="button" onClick={() => setLevel(l)} className={cn("flex-1 rounded-md border px-3 py-2 text-sm", level === l ? "border-accent bg-accent-soft" : "border-border text-fg-muted hover:text-fg")}>{t(`support.level.${l}`)}</button>
                  ))}
                </div>
              </Field>
              <Field label={t("support.project")}>
                <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                  <option value="">{t("support.allProjects")}</option>
                  {projects.map((p: Project) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Select>
              </Field>
              <Field label={t("support.minutes")}>
                <Input type="number" min={5} max={480} value={minutes ?? o.defaultMinutes} onChange={(e) => setMinutes(Number(e.target.value) || null)} />
              </Field>
            </div>
            <p className="text-xs text-fg-muted">{level === "act" ? t("support.actHint") : t("support.readHint")}</p>
            <Field label={t("support.code")}>
              <CodeInput required value={code} onChange={setCode} className="max-w-xs" />
            </Field>
            {unlock.error && <Alert tone="danger">{toMsg(unlock.error)}</Alert>}
            <Button type="submit" loading={unlock.isPending} disabled={code.length !== 6}><LockOpen className="size-4" /> {t("support.unlock")}</Button>
          </form>
        </Card>
      )}

      {/* keys */}
      <Card className="space-y-3 p-4">
        <h3 className="flex items-center gap-2 text-sm font-medium"><KeyRound className="size-4 text-accent" /> {t("support.keys")}</h3>
        <p className="text-xs text-fg-muted">{t("support.keysHint")}</p>
        {o.keys.map((k) => (
          <div key={k.id} className={cn("flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2 text-sm", k.revokedAt && "opacity-50")}>
            <span dir="auto" className="font-medium">{k.name}</span>
            <code dir="ltr" className="rounded bg-surface-2 px-1.5 text-xs">{k.prefix}…</code>
            <span className="text-xs text-fg-muted">{k.revokedAt ? t("support.revoked") : k.lastUsedAt ? t("support.lastUsed", { at: new Date(k.lastUsedAt).toLocaleString() }) : t("support.neverUsed")}</span>
            {!k.revokedAt && <Button size="sm" variant="ghost" className="ms-auto" onClick={async () => { if (await confirm({ title: t("support.revokeTitle"), message: t("support.revokeMsg", { name: k.name }), confirmLabel: t("support.revoke"), danger: true })) revoke.mutate(k.id); }}><Trash2 className="size-3.5" /></Button>}
          </div>
        ))}
        {shownKey ? (
          <div className="space-y-2 rounded-md border border-warning/50 bg-warning/10 p-3">
            <p className="text-sm font-medium">{t("support.keyOnce")}</p>
            <div className="flex items-center gap-2">
              <code dir="ltr" className="min-w-0 flex-1 break-all rounded bg-bg px-2 py-1 text-xs">{shownKey}</code>
              <Button size="sm" variant="secondary" onClick={() => void copyText(shownKey)}><Copy className="size-3.5" /> {t("common.copy")}</Button>
            </div>
            <p className="text-xs text-fg-muted">{t("support.keyWhere")}</p>
            <Button size="sm" variant="ghost" onClick={() => setShownKey(null)}>{t("support.done")}</Button>
          </div>
        ) : (
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (newName.trim()) create.mutate(); }}>
            <Input dir="auto" value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={60} className="max-w-xs" />
            <Button type="submit" variant="secondary" loading={create.isPending}><KeyRound className="size-4" /> {t("support.newKey")}</Button>
          </form>
        )}
      </Card>

      {/* default duration */}
      <Card className="p-4">
        <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); if (defMinutes) saveMinutes.mutate(defMinutes); }}>
          <Field label={t("support.defaultMinutes")}>
            <Input type="number" min={5} max={480} className="w-32" value={defMinutes ?? o.defaultMinutes} onChange={(e) => setDefMinutes(Number(e.target.value) || null)} />
          </Field>
          <Button type="submit" variant="secondary" loading={saveMinutes.isPending} disabled={!defMinutes || defMinutes === o.defaultMinutes}>{t("common.save")}</Button>
        </form>
      </Card>

      {/* what was done */}
      <Card className="p-4">
        <h3 className="mb-2 text-sm font-medium">{t("support.activity")}</h3>
        {o.activity.length === 0 ? <p className="text-xs text-fg-muted">{t("support.noActivity")}</p> : (
          <ul className="divide-y divide-border text-xs">
            {o.activity.map((a) => (
              <li key={a.id} className="flex flex-wrap items-baseline gap-x-2 py-1.5">
                <time className="shrink-0 tabular-nums text-fg-muted">{new Date(a.at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}</time>
                <span className={cn("shrink-0 font-medium", a.action === "support.act" ? "text-danger" : a.action === "support.unlock_failed" ? "text-warning" : "")}>{t(`support.actions.${a.action.replace("support.", "")}`, { defaultValue: a.action })}</span>
                {a.projectName && <span className="text-fg-muted">· {a.projectName}</span>}
                <span dir="auto" className="min-w-0 basis-full text-fg-muted sm:basis-auto">{a.detail}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
